import React, { useCallback, useContext, useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import moment from 'moment'

// LuxKit Components
import BodyText from 'LuxKit/Typography/BodyText'
import TextLink from 'LuxKit/TextLink'
import Group from 'components/utils/Group'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import CardCarousel from 'LuxKit/Carousel/CardCarousel'
import Caption from 'LuxKit/Typography/Caption'
import FormatCurrency from 'components/Common/FormatCurrency'
import Heading from 'LuxKit/Typography/Heading'
import { buildSearchParamsForFilterPane } from 'components/Pages/DestinationLandingPage/DestinationLandingPageUtils'
import Places from 'constants/places'
import Clickable from 'components/Common/Clickable'
import interactiveFocusStyle from 'styles/utils/interactiveFocusStyle'
import { mediaHoverable } from 'lib/theme/mediaQueries'
import cn from 'clsx'
import MaldivesDirectSearchFlightCalendarItem from './MaldivesDirectSearchFlightCalendarItem'
import MaldivesOfferCalendarItem from './MaldivesOfferCalendarItem'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { fetchFlights } from 'actions/FlightActions'
import { fetchOfferList } from 'actions/OfferActions'
import getOfferListKey from 'lib/offer/offerListKey'
import { getFlightSearchKey } from 'lib/flights/flightUtils'
import { buildFlightSearchPayload, buildBundleOfferFilters } from '../MaldivesDirectUtils'
import { HOTEL_OFFERS_LIMIT, MALDIVES_BUNDLE_DEFAULT_NIGHTS, MALDIVES_CHARTER_CONFIG } from 'constants/charterFlight'
import { sumUpOccupancies } from 'lib/offer/occupancyUtils'
import { FlightsClassTypes } from 'constants/flight'
import CurrencyContext from 'contexts/currencyContext'
import { CalendarItemBundleData } from '../useFlightCalendarData'

// Types
interface DateRange {
  departureDate: string
  returnDate: string
  checkIn: string
  checkOut: string
  durationDays: number // Total days from departureDate to returnDate
  nights: number // Nights from checkIn to checkOut
}

interface TripOption {
  id: string
  departDate: string
  returnDate: string
  checkIn: string
  checkOut: string
  price: number
  memberPrice?: number
  currency: string
  seatsLeft?: number
  nights: number
  available: boolean
  month?: string
}

interface Props {
  onDateSelect: (option: TripOption) => void
  selectedOptionId?: string
  nights?: number
  onDurationChange?: (nights: number) => void
  occupancies?: Array<App.Occupants>
  fareClass: FlightsClassTypes
  priceMode: 'total' | 'perPerson'
  // LuxPlus related
  luxPlusEnabled?: boolean
  onLuxPlusToggle?: (enabled: boolean) => void
  // Duration options for the dropdown (optional - managed by parent modal header)
  durationOptions?: ReadonlyArray<{ value: number, label: string }>
  dateRangesByMonth: Record<string, Array<DateRange>>
  selectedMonth: string
  onMonthChange: (month: string) => void
  /** Ready-to-use calendar item data - no calculation needed **/
  itemBundleDataByDate?: Record<string, CalendarItemBundleData>
  mode: 'search' | 'offer'
}

interface DateItem {
  id: string
  checkIn: string
  checkOut: string
  departDate: string
  returnDate: string
  month: string
}

interface TripOptionsListProps {
  currentMonthTripOptions: Array<DateItem>
  selectedMonth: string
  luxPlusEnabled: boolean
  selectedOptionId?: string
  onOptionSelect: (option: TripOption) => void
  nights: number
  rooms: Array<App.Occupants>
  fareClass: FlightsClassTypes
  priceMode: 'total' | 'perPerson'
  mode: 'search' | 'offer'
  itemBundleDataByDate?: Record<string, CalendarItemBundleData>
}

// Styled Components - Improved Month Selector (inspired by TourV2)
const MonthTile = styled(Clickable)`
  position: relative;
  padding: ${rem(8)} ${rem(12)};
  text-align: center;
  border: 1px solid ${props => props.theme.palette.neutral.default.five};
  height: ${rem(80)};
  min-width: ${rem(104)};
  background-color: ${props => props.theme.palette.neutral.default.eight};
  transition: border-color 0.2s, background-color 0.2s;

  &:focus {
    ${interactiveFocusStyle}
  }

  ${mediaHoverable} {
    &:hover {
      background-color: ${props => props.theme.palette.neutral.default.seven};
    }
  }

  &.selected {
    border: 1px solid ${props => props.theme.palette.neutral.default.one};
  }

  &.cheapest {
    border-color: ${props => props.theme.palette.highlight.secondary.normalBackground};
  }
`

const StyledCardCarousel = styled(CardCarousel)`
  margin-top: ${rem(16)};
`

const TripOptionsHeader = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 2fr;
  gap: ${rem(24)};
  padding: ${rem(16)} ${rem(24)};
  margin-top: ${rem(24)};
  border-bottom: 1px solid ${props => props.theme.palette.neutral.default.six};
`

const EmptyStateGroup = styled(Group)`
  padding: ${rem(32)};
`

// Child Components
const TripOptionsList = React.memo(function TripOptionsList(props: TripOptionsListProps) {
  const {
    currentMonthTripOptions,
    selectedMonth,
    luxPlusEnabled,
    selectedOptionId,
    onOptionSelect,
    nights,
    rooms,
    fareClass,
    priceMode,
    mode,
    itemBundleDataByDate,
  } = props

  if (currentMonthTripOptions.length === 0) {
    return (
      <EmptyStateGroup direction="vertical" horizontalAlign="center" gap={8}>
        <Heading variant="heading5" align="center">
          No departures available for {selectedMonth}
        </Heading>
        <BodyText variant="medium" align="center" colour="neutral-three">
          Try selecting a different month or adjusting your search criteria
        </BodyText>
      </EmptyStateGroup>
    )
  }

  return (
    <>
      {currentMonthTripOptions.map((item) => {
        const isSelected = item.id === selectedOptionId
        const isVisible = item.month === selectedMonth
        const commonProps = {
          key: item.id,
          checkIn: item.checkIn,
          checkOut: item.checkOut,
          departDate: item.departDate,
          returnDate: item.returnDate,
          nights: nights,
          priceMode: priceMode,
          luxPlusEnabled: luxPlusEnabled,
          isSelected: isSelected,
          isVisible: isVisible,
          onSelect: onOptionSelect,
        }

        switch (mode) {
          case 'search':
            return (
              <MaldivesDirectSearchFlightCalendarItem
                {...commonProps}
                rooms={rooms}
                fareClass={fareClass}
              />
            )
          case 'offer': {
            // Get pre-calculated bundle data for this date
            const itemBundleData = itemBundleDataByDate?.[item.checkIn]

            // Fallback to default empty state if data is missing
            const itemData: CalendarItemBundleData = itemBundleData ?? {
              fetching: true,
              available: false,
              totalPrice: 0,
              pricePerPerson: 0,
              offerCount: 0,
              seatsLeft: 0,
            }

            return (
              <MaldivesOfferCalendarItem
                {...commonProps}
                bundleData={itemData}
              />
            )
          }
          default:
            return null
        }
      })}
    </>
  )
})

// Main Component
function MaldivesDirectSearchFlightCalendar(props: Props) {
  const {
    onDateSelect,
    selectedOptionId,
    nights = MALDIVES_BUNDLE_DEFAULT_NIGHTS,
    occupancies,
    fareClass,
    priceMode,
    luxPlusEnabled = true,
    dateRangesByMonth,
    selectedMonth,
    onMonthChange,
    mode,
    itemBundleDataByDate,
  } = props

  const dispatch = useAppDispatch()
  const allOfferLists = useAppSelector(state => state.offer.offerLists)
  const allOffers = useAppSelector(state => state.offer.offers)
  const allFlights = useAppSelector(state => state.flights.searchV2Flights)

  // Calculate total passengers for bundle pricing
  const totalPassengers = useMemo(
    () => sumUpOccupancies(occupancies),
    [occupancies]
  )

  const currencyContext = useContext(CurrencyContext)

  // Prefetch data for all date ranges to enable month price calculation in search mode
  React.useEffect(() => {
    if (mode !== 'search') return

    Object.values(dateRangesByMonth).flat().forEach(({ departureDate, returnDate, checkIn, checkOut }) => {
      // Load hotel offers
      const hotelFilters = buildBundleOfferFilters({
        destinationId: Places.Maldives.id,
        checkIn: checkIn,
        checkOut: checkOut,
        rooms: occupancies || [{ adults: 2, children: 0 }],
        limit: HOTEL_OFFERS_LIMIT,
        isPricePerPerson: priceMode === 'perPerson',
        fareClass,
      })
      dispatch(fetchOfferList(hotelFilters, { clientCheckAvailability: true }))

      // Load flight data
      const flightParams = buildFlightSearchPayload({
        departureAirport: MALDIVES_CHARTER_CONFIG.DEPARTURE_AIRPORT,
        arrivalAirport: MALDIVES_CHARTER_CONFIG.ARRIVAL_AIRPORT,
        departureDate: departureDate,
        returnDate: returnDate,
        occupancies: occupancies || [{ adults: 2, children: 0 }],
        fareClass: fareClass,
        currency: currencyContext || 'AUD',
      })
      dispatch(fetchFlights(flightParams))
    })
  }, [dispatch, dateRangesByMonth, occupancies, fareClass, priceMode, currencyContext, mode])
  // Generate months for the selector with bundle pricing
  const months = useMemo(() => {
    const monthNames: Array<{
      name: string
      price: number
      fetching: boolean
      monthLabel: string
      year: number
    }> = []

    Object.entries(dateRangesByMonth).forEach(([monthName, dateRanges]) => {
      // Skip months with no date entries
      if (dateRanges.length === 0) return

      // Calculate lowest bundle price per person for this month
      let lowestPrice = 0
      let isMonthFetching = false

      dateRanges.forEach(({ departureDate, returnDate, checkIn, checkOut }) => {
        if (mode === 'search') {
          // Search mode: Get data from Redux (loaded by CalendarItems)
          // Use checkIn/checkOut (inner dates) for hotel queries
          const hotelFilters: App.OfferListFilters = buildBundleOfferFilters({
            destinationId: Places.Maldives.id,
            checkIn: checkIn,
            checkOut: checkOut,
            rooms: occupancies || [{ adults: 2, children: 0 }],
            limit: HOTEL_OFFERS_LIMIT,
            isPricePerPerson: priceMode === 'perPerson',
            fareClass,
          })

          // Use departureDate/returnDate (outer dates) for flight queries
          const flightParams = buildFlightSearchPayload({
            departureAirport: MALDIVES_CHARTER_CONFIG.DEPARTURE_AIRPORT,
            arrivalAirport: MALDIVES_CHARTER_CONFIG.ARRIVAL_AIRPORT,
            departureDate: departureDate,
            returnDate: returnDate,
            occupancies: occupancies || [{ adults: 2, children: 0 }],
            fareClass: fareClass,
            currency: currencyContext || 'AUD',
          })

          const hotelKey = getOfferListKey(hotelFilters)
          const flightKey = getFlightSearchKey(flightParams)

          const offerList = allOfferLists[hotelKey]
          const flightData = allFlights[flightKey]

          // Check if still fetching
          if (offerList?.fetching || flightData?.fetching) {
            isMonthFetching = true
          }

          // Calculate hotel price
          const offerIds = offerList?.offerIds || []
          const hotelPrices = offerIds
            .map(offerId => {
              const offer = allOffers[offerId] as App.HotelOffer | undefined
              if (!offer) return null
              const pkg = offer.packages.find(p => p.duration === nights)
              return luxPlusEnabled ? (pkg?.memberPrice || pkg?.price) : pkg?.price
            })
            .filter((p): p is number => typeof p === 'number' && p > 0)
          const lowestHotelPrice = hotelPrices.length > 0 ? Math.min(...hotelPrices) : 0

          // Calculate flight price
          const departingFlight = flightData?.fares?.[0]?.[0]
          const returningFlight = flightData?.fares?.[1]?.[0]
          const departingFare = departingFlight?.price?.all?.totalFare ?? 0
          const returningFare = returningFlight?.price?.all?.totalFare ?? 0
          const totalFlightFare = departingFare + returningFare

          // Calculate bundle price based on price mode
          if (lowestHotelPrice > 0 && totalFlightFare > 0 && !offerList?.fetching && !flightData?.fetching) {
            const bundleTotal = lowestHotelPrice + totalFlightFare
            const bundlePrice = priceMode === 'perPerson' && totalPassengers > 0
              ? bundleTotal / totalPassengers
              : bundleTotal

            if (bundlePrice > 0 && (lowestPrice === 0 || bundlePrice < lowestPrice)) {
              lowestPrice = bundlePrice
            }
          }
        } else {
          // Offer mode: Get pre-calculated bundle data
          const itemData = itemBundleDataByDate?.[checkIn]

          // Check if still fetching
          if (itemData?.fetching) {
            isMonthFetching = true
          }

          // Calculate bundle price based on priceMode and luxPlusEnabled
          const bundlePrice = priceMode === 'total'
            ? (luxPlusEnabled ? (itemData?.memberTotalPrice || itemData?.totalPrice) : itemData?.totalPrice)
            : (luxPlusEnabled ? (itemData?.memberPricePerPerson || itemData?.pricePerPerson) : itemData?.pricePerPerson)

          if (itemData?.available && bundlePrice && bundlePrice > 0) {
            if (lowestPrice === 0 || bundlePrice < lowestPrice) {
              lowestPrice = bundlePrice
            }
          }
        }
      })

      // Extract month and year for display
      const firstDate = dateRanges[0]?.checkIn
      const date = moment(firstDate)

      monthNames.push({
        name: monthName,
        price: lowestPrice || 0,
        fetching: isMonthFetching,
        monthLabel: date.format('MMM'),
        year: date.year(),
      })
    })

    return monthNames
  }, [dateRangesByMonth, nights, luxPlusEnabled, totalPassengers, allOfferLists, allOffers, allFlights, fareClass, occupancies, priceMode, currencyContext, mode, itemBundleDataByDate])

  // Find the cheapest month for highlighting
  const cheapestMonthPrice = useMemo(() => {
    const prices = months.filter(m => m.price > 0).map(m => m.price)
    return prices.length > 0 ? Math.min(...prices) : 0
  }, [months])

  // Generate all date items for ALL months (lightweight - just dates)
  const allDateItems = useMemo(() => {
    const items: Array<DateItem> = []

    // Loop through all months
    Object.entries(dateRangesByMonth).forEach(([monthName, dateRanges]) => {
      // Skip months with no date entries
      if (dateRanges.length === 0) return

      dateRanges.forEach(({ departureDate, returnDate, checkIn, checkOut }) => {
        items.push({
          id: `${checkIn}-${nights}n`,
          checkIn: checkIn,
          checkOut: checkOut,
          departDate: departureDate,
          returnDate: returnDate,
          month: monthName,
        })
      })
    })

    return items
  }, [dateRangesByMonth, nights])

  const handleMonthSelect = useCallback((month: string) => {
    onMonthChange(month)
  }, [onMonthChange])

  const handleOptionSelect = useCallback((option: TripOption) => {
    onDateSelect(option)
  }, [onDateSelect])

  return (
    <VerticalSpacer gap={4}>
      {/* Month Selector - Improved Carousel */}
      <StyledCardCarousel
        gap={12}
        alignment="center"
        snap="start"
        gutterSize={0}
        gutterStyle="overflow"
      >
        {months.map((month) => {
          const isCheapest = month.price > 0 && month.price === cheapestMonthPrice
          const priceColour = isCheapest ? 'highlight-secondary' : (luxPlusEnabled ? 'lux-plus' : undefined)
          return (
            <MonthTile
              key={month.name}
              onClick={() => handleMonthSelect(month.name)}
              className={cn({
                selected: selectedMonth === month.name,
                cheapest: isCheapest,
              })}
            >
              <BodyText variant="small" colour="neutral-three">
                {month.year}
              </BodyText>
              <BodyText variant="large">{month.monthLabel}</BodyText>
              <BodyText variant="large" weight="bold" colour={priceColour}>
                {month.fetching && <Caption variant="small">Loading...</Caption>}
                {!month.fetching && month.price > 0 && <FormatCurrency value={month.price} format="roundedDollar" />}
                {!month.fetching && !month.price && <Caption variant="small">Unavailable</Caption>}
              </BodyText>
            </MonthTile>
          )
        })}
      </StyledCardCarousel>

      <TripOptionsHeader>
        <BodyText variant="small" colour="neutral-three">Depart</BodyText>
        <BodyText variant="small" colour="neutral-three">Return</BodyText>
        <BodyText variant="small" colour="neutral-three"></BodyText>
        <BodyText variant="small" colour="neutral-three">Flight + Hotel price</BodyText>
      </TripOptionsHeader>

      <TripOptionsList
        currentMonthTripOptions={allDateItems}
        selectedMonth={selectedMonth}
        luxPlusEnabled={luxPlusEnabled}
        selectedOptionId={selectedOptionId}
        onOptionSelect={handleOptionSelect}
        nights={nights}
        rooms={occupancies || [{ adults: 2, children: 0 }]}
        fareClass={fareClass}
        priceMode={priceMode}
        mode={mode}
        itemBundleDataByDate={itemBundleDataByDate}
      />

      <Group direction="horizontal" horizontalAlign="center">
        <BodyText variant="medium" colour="neutral-two">
          <strong>Looking for other dates?</strong> <TextLink
            weight="regular"
            to={`/search?${buildSearchParamsForFilterPane({
              originalSearch: '',
              destinationName: Places.Maldives.name,
              destinationId: Places.Maldives.id,
              adults: 2,
              children: 'none'
            })}`}
          >
            Explore more Maldives offers.
          </TextLink>
        </BodyText>
      </Group>
    </VerticalSpacer>
  )
}

export default MaldivesDirectSearchFlightCalendar
